/*==============================================================
  NP CAROUSEL COMPONENT JS
  Namespaced, self-contained carousel controller.
  - No globals, no external libraries.
  - One instance per section; multiple sections work independently.
  - Native overflow-scroll based: gives free touch swipe support,
    then layers keyboard, buttons, mouse-drag and wheel-to-horizontal
    behaviour on top.
==============================================================*/

(function () {
  "use strict";

  var SELECTOR_ROOT = "[data-np-carousel]";
  var INITIALISED_FLAG = "npCarouselInitialised";

  /**
   * Controller for a single carousel instance.
   * @param {HTMLElement} root - the section element with [data-np-carousel]
   */
  function NPCarousel(root) {
    if (!root || root.dataset[INITIALISED_FLAG] === "true") return;

    this.root = root;
    this.track = root.querySelector("[data-np-carousel-track]");
    this.prevBtn = root.querySelector("[data-np-carousel-prev]");
    this.nextBtn = root.querySelector("[data-np-carousel-next]");
    this.items = Array.prototype.slice.call(root.querySelectorAll("[data-np-carousel-item]"));

    if (!this.track || this.items.length === 0) return;

    this.isDragging = false;
    this.dragMoved = false;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;

    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    this._bindHandlers();
    this._attachEvents();
    this._updateNavState();

    root.dataset[INITIALISED_FLAG] = "true";
  }

  NPCarousel.prototype._bindHandlers = function () {
    // Bind once so add/removeEventListener reference the same function.
    this.onPrevClick = this._handlePrevClick.bind(this);
    this.onNextClick = this._handleNextClick.bind(this);
    this.onNavMouseDown = this._handleNavMouseDown.bind(this);
    this.onWheel = this._handleWheel.bind(this);
    this.onPointerDown = this._handlePointerDown.bind(this);
    this.onPointerMove = this._handlePointerMove.bind(this);
    this.onPointerUp = this._handlePointerUp.bind(this);
    this.onKeyDown = this._handleKeyDown.bind(this);
    this.onScroll = this._handleScroll.bind(this);
    this.onClickCapture = this._handleClickCapture.bind(this);
  };

  NPCarousel.prototype._attachEvents = function () {
    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", this.onPrevClick);
      this.prevBtn.addEventListener("mousedown", this.onNavMouseDown);
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", this.onNextClick);
      this.nextBtn.addEventListener("mousedown", this.onNavMouseDown);
    }

    this.track.addEventListener("wheel", this.onWheel, { passive: false });
    this.track.addEventListener("pointerdown", this.onPointerDown);
    this.track.addEventListener("keydown", this.onKeyDown);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    // Suppress accidental link navigation after a drag.
    this.track.addEventListener("click", this.onClickCapture, true);
  };

  NPCarousel.prototype._isAtStart = function () {
    return this.track.scrollLeft <= 1;
  };

  NPCarousel.prototype._isAtEnd = function () {
    return (
      this.track.scrollLeft >=
      this.track.scrollWidth - this.track.clientWidth - 1
    );
  };

  /**
   * Returns the index of the item currently at (or nearest to) the start
   * edge of the visible track, measured from actual rendered positions
   * rather than an assumed pixel step, so prev/next stay lined up with
   * each card's real snap position after repeated clicks.
   */
  NPCarousel.prototype._getCurrentIndex = function () {
    var trackRect = this.track.getBoundingClientRect();
    var closestIndex = 0;
    var closestDiff = Infinity;

    for (var i = 0; i < this.items.length; i++) {
      var itemRect = this.items[i].getBoundingClientRect();
      var diff = Math.abs(itemRect.left - trackRect.left);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }

    return closestIndex;
  };

  /**
   * Scrolls the track (and only the track) so the given item sits at the
   * start edge. Deliberately avoids scrollIntoView(), which also scrolls
   * the page vertically if the card isn't fully on screen.
   */
  NPCarousel.prototype._scrollToIndex = function (index) {
    var clamped = Math.max(0, Math.min(index, this.items.length - 1));
    var targetItem = this.items[clamped];
    if (!targetItem) return;

    var trackRect = this.track.getBoundingClientRect();
    var itemRect = targetItem.getBoundingClientRect();
    var targetLeft = this.track.scrollLeft + (itemRect.left - trackRect.left);

    this.track.scrollTo({
      left: targetLeft,
      behavior: this.prefersReducedMotion ? "auto" : "smooth",
    });
  };

  NPCarousel.prototype._handlePrevClick = function (event) {
    if (event) event.preventDefault();
    this._scrollToIndex(this._getCurrentIndex() - 1);
  };

  NPCarousel.prototype._handleNextClick = function (event) {
    if (event) event.preventDefault();
    this._scrollToIndex(this._getCurrentIndex() + 1);
  };

  /**
   * Stops taps/clicks from focusing the arrow buttons. On some mobile
   * browsers, focusing a button makes the page scroll slightly to bring
   * it fully into view (e.g. under a sticky header). The click itself
   * still fires, and keyboard users can still Tab to the buttons.
   */
  NPCarousel.prototype._handleNavMouseDown = function (event) {
    event.preventDefault();
  };

  /**
   * Wheel handling:
   * - Only horizontal-dominant gestures (trackpad / Magic Mouse sideways
   *   swipes) are redirected into the carousel's horizontal scroll.
   * - Vertical wheel input is left alone so the page scrolls normally.
   * - Once an edge is reached in the direction of travel, the event is
   *   NOT prevented, so normal page scroll resumes automatically.
   */
  NPCarousel.prototype._handleWheel = function (event) {
    var hasOverflow = this.track.scrollWidth > this.track.clientWidth;
    if (!hasOverflow) return;

    var deltaX = event.deltaX;
    var deltaY = event.deltaY;

    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (deltaX === 0) return;

    var movingBackward = deltaX < 0;
    var movingForward = deltaX > 0;

    if ((movingBackward && this._isAtStart()) || (movingForward && this._isAtEnd())) {
      return;
    }

    event.preventDefault();
    this.track.scrollLeft += deltaX;

    // Suspend scroll-snap during the gesture (via .is-wheeling in CSS),
    // then let it settle on a card once the gesture goes idle.
    this.track.classList.add("is-wheeling");
    if (this._wheelEndTimeout) window.clearTimeout(this._wheelEndTimeout);
    var self = this;
    this._wheelEndTimeout = window.setTimeout(function () {
      self.track.classList.remove("is-wheeling");
    }, 150);
  };

  /**
   * Mouse-drag support. Touch devices use native swipe (overflow-x +
   * touch-action in CSS), so dragging is limited to mouse pointers.
   * setPointerCapture() is deliberately not used: it can redirect the
   * resulting click away from card links in some browsers.
   */
  NPCarousel.prototype._handlePointerDown = function (event) {
    if (event.pointerType !== "mouse") return;

    this.isDragging = true;
    this.dragMoved = false;
    this.dragStartX = event.pageX;
    this.dragStartScrollLeft = this.track.scrollLeft;
    this.track.classList.add("is-dragging");

    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
  };

  NPCarousel.prototype._handlePointerMove = function (event) {
    if (!this.isDragging) return;

    var deltaX = event.pageX - this.dragStartX;
    // 8px threshold so normal click jitter isn't treated as a drag.
    if (Math.abs(deltaX) > 8) this.dragMoved = true;

    this.track.scrollLeft = this.dragStartScrollLeft - deltaX;
  };

  NPCarousel.prototype._handlePointerUp = function () {
    this.isDragging = false;
    this.track.classList.remove("is-dragging");
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
  };

  /**
   * Prevents a link/button inside an item from firing its click when the
   * user was actually dragging the carousel.
   */
  NPCarousel.prototype._handleClickCapture = function (event) {
    if (this.dragMoved) {
      event.preventDefault();
      event.stopPropagation();
      this.dragMoved = false;
    }
  };

  /**
   * Keyboard navigation: Left/Right move one card, Home/End jump to
   * either end. Works when the track itself is focused.
   */
  NPCarousel.prototype._handleKeyDown = function (event) {
    var behavior = this.prefersReducedMotion ? "auto" : "smooth";

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        this._scrollToIndex(this._getCurrentIndex() - 1);
        break;
      case "ArrowRight":
        event.preventDefault();
        this._scrollToIndex(this._getCurrentIndex() + 1);
        break;
      case "Home":
        event.preventDefault();
        this.track.scrollTo({ left: 0, behavior: behavior });
        break;
      case "End":
        event.preventDefault();
        this.track.scrollTo({ left: this.track.scrollWidth, behavior: behavior });
        break;
      default:
        break;
    }
  };

  /**
   * Keeps prev/next button state in sync with scroll position.
   */
  NPCarousel.prototype._handleScroll = function () {
    var self = this;
    if (this._scrollRaf) window.cancelAnimationFrame(this._scrollRaf);
    this._scrollRaf = window.requestAnimationFrame(function () {
      self._updateNavState();
    });
  };

  NPCarousel.prototype._updateNavState = function () {
    var atStart = this._isAtStart();
    var atEnd = this._isAtEnd();

    // Only show navigation if the carousel can actually scroll.
    var hasOverflow = this.track.scrollWidth > this.track.clientWidth;

    if (this.prevBtn) {
      this.prevBtn.hidden = !hasOverflow;
      this.prevBtn.setAttribute("aria-disabled", atStart ? "true" : "false");
    }

    if (this.nextBtn) {
      this.nextBtn.hidden = !hasOverflow;
      this.nextBtn.setAttribute("aria-disabled", atEnd ? "true" : "false");
    }
  };

  /**
   * Scrolls a specific item into view. Only used when a merchant selects
   * a block in the Theme Editor, where moving the page is helpful.
   */
  NPCarousel.prototype.scrollItemIntoView = function (itemEl) {
    if (!itemEl) return;
    itemEl.scrollIntoView({
      behavior: this.prefersReducedMotion ? "auto" : "smooth",
      inline: "start",
      block: "nearest",
    });
  };

  NPCarousel.prototype.destroy = function () {
    if (this._wheelEndTimeout) window.clearTimeout(this._wheelEndTimeout);
    if (this._scrollRaf) window.cancelAnimationFrame(this._scrollRaf);

    if (this.prevBtn) {
      this.prevBtn.removeEventListener("click", this.onPrevClick);
      this.prevBtn.removeEventListener("mousedown", this.onNavMouseDown);
    }
    if (this.nextBtn) {
      this.nextBtn.removeEventListener("click", this.onNextClick);
      this.nextBtn.removeEventListener("mousedown", this.onNavMouseDown);
    }

    this.track.removeEventListener("wheel", this.onWheel);
    this.track.removeEventListener("pointerdown", this.onPointerDown);
    this.track.removeEventListener("keydown", this.onKeyDown);
    this.track.removeEventListener("scroll", this.onScroll);
    this.track.removeEventListener("click", this.onClickCapture, true);

    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);

    delete this.root.dataset[INITIALISED_FLAG];
  };

  /* ------------------------------------------------------------
     Instance registry (module-scoped, not global) so the Theme
     Editor can find/destroy/re-init a specific section's instance.
  ------------------------------------------------------------ */
  var instances = new Map();

  function initRoot(root) {
    if (!root) return;
    var instance = new NPCarousel(root);
    instances.set(root, instance);
  }

  function initAll(context) {
    var scope = context || document;
    var roots = scope.querySelectorAll ? scope.querySelectorAll(SELECTOR_ROOT) : [];
    Array.prototype.forEach.call(roots, initRoot);
  }

  function destroyRoot(root) {
    var instance = instances.get(root);
    if (instance) {
      instance.destroy();
      instances.delete(root);
    }
  }

  function findRoot(target) {
    if (!target) return null;
    return target.matches && target.matches(SELECTOR_ROOT)
      ? target
      : target.querySelector(SELECTOR_ROOT);
  }

  /* ------------------------------------------------------------
     Bootstrapping + Shopify Theme Editor integration
  ------------------------------------------------------------ */

  document.addEventListener("DOMContentLoaded", function () {
    initAll(document);
  });

  // Re-init when a section is added/re-rendered in the Theme Editor.
  document.addEventListener("shopify:section:load", function (event) {
    initRoot(findRoot(event.target));
  });

  // Clean up listeners when a section is removed in the Theme Editor.
  document.addEventListener("shopify:section:unload", function (event) {
    destroyRoot(findRoot(event.target));
  });

  // Scroll the selected block into view while editing.
  document.addEventListener("shopify:block:select", function (event) {
    var itemEl = event.target;
    if (!itemEl || !itemEl.hasAttribute("data-np-carousel-item")) return;

    var root = itemEl.closest(SELECTOR_ROOT);
    var instance = root ? instances.get(root) : null;
    if (instance) instance.scrollItemIntoView(itemEl);
  });
})();